import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  User, 
  Mail, 
  Phone, 
  CheckCircle2, 
  ArrowRight, 
  Loader2,
  Globe2,
  Star,
  Sparkles,
  Gift,
  Target,
  TrendingUp,
  Award,
  GraduationCap,
  BookOpen,
  Users,
  Zap,
} from 'lucide-react';
import { saveLead } from '../services/leadService';
import { getPopupSettings } from '../services/apiService';

const RandomPopup = ({ 
  onClose = () => {}, 
  templateId, 
  isPreview = false, 
  forceOpen = false,
  customTemplateData = null 
}) => {
  const [isOpen, setIsOpen] = useState(forceOpen || !isPreview);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Auto-open when component is rendered (unless it's a preview)
  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
    } else if (!isPreview) {
      // Auto-open when rendered normally (not preview mode)
      setIsOpen(true);
    }
  }, [forceOpen, isPreview]);
  
  // -- TEMPLATE DEFINITIONS (Source of Truth for Colors/Themes) --
  const defaultTemplates = [
    { 
      id: 'template1', 
      title: "Don't let your dream wait.", 
      subtitle: 'Get a personalized roadmap to top universities.',
      color: 'from-red-600 to-red-700',
      icon: Globe2,
      tag: 'Global Admissions',
      theme: {
        button: 'bg-red-600 hover:bg-red-700',
        ring: 'focus:ring-red-600',
        border: 'focus:border-red-600',
        iconColor: 'text-red-600',
        lightBg: 'bg-red-50'
      }
    },
    { 
      id: 'template2', 
      title: 'Join 5,000+ Successful Students', 
      subtitle: 'Your success story starts here.',
      color: 'from-blue-600 to-blue-700',
      icon: Gift,
      tag: 'Success Story',
      theme: {
        button: 'bg-blue-600 hover:bg-blue-700',
        ring: 'focus:ring-blue-600',
        border: 'focus:border-blue-600',
        iconColor: 'text-blue-600',
        lightBg: 'bg-blue-50'
      }
    },
    { 
      id: 'template3', 
      title: 'Score Your Target Band', 
      subtitle: 'Crack IELTS with 8+ Band Score',
      color: 'from-purple-600 to-purple-700',
      icon: Target,
      tag: 'IELTS Masterclass',
      theme: {
        button: 'bg-purple-600 hover:bg-purple-700',
        ring: 'focus:ring-purple-600',
        border: 'focus:border-purple-600',
        iconColor: 'text-purple-600',
        lightBg: 'bg-purple-50'
      }
    },
    { 
      id: 'template4', 
      title: 'Exclusive Offer', 
      subtitle: 'Free Profile Evaluation + Scholarship Guidance',
      color: 'from-emerald-600 to-emerald-700',
      icon: TrendingUp,
      tag: 'Limited Time',
      theme: {
        button: 'bg-emerald-600 hover:bg-emerald-700',
        ring: 'focus:ring-emerald-600',
        border: 'focus:border-emerald-600',
        iconColor: 'text-emerald-600',
        lightBg: 'bg-emerald-50'
      }
    },
    { 
      id: 'template5', 
      title: 'Achieve Your Target Band', 
      subtitle: 'Join 2,000+ students who achieved their dream score.',
      color: 'from-orange-600 to-orange-700',
      icon: BookOpen,
      tag: 'IELTS Training',
      theme: {
        button: 'bg-orange-600 hover:bg-orange-700',
        ring: 'focus:ring-orange-600',
        border: 'focus:border-orange-600',
        iconColor: 'text-orange-600',
        lightBg: 'bg-orange-50'
      }
    },
    { 
      id: 'template6', 
      title: 'Join 10,000+ Students', 
      subtitle: "Be part of India's largest study abroad community.",
      color: 'from-indigo-600 to-indigo-700',
      icon: Users,
      tag: 'Community',
      theme: {
        button: 'bg-indigo-600 hover:bg-indigo-700',
        ring: 'focus:ring-indigo-600',
        border: 'focus:border-indigo-600',
        iconColor: 'text-indigo-600',
        lightBg: 'bg-indigo-50'
      }
    },
    { 
      id: 'template7', 
      title: 'Unlock Scholarships Worth ₹50L+', 
      subtitle: 'Our students have secured over ₹5 Crores.',
      color: 'from-pink-600 to-pink-700',
      icon: Award,
      tag: 'Scholarships',
      theme: {
        button: 'bg-pink-600 hover:bg-pink-700',
        ring: 'focus:ring-pink-600',
        border: 'focus:border-pink-600',
        iconColor: 'text-pink-600',
        lightBg: 'bg-pink-50'
      }
    },
    { 
      id: 'template8', 
      title: 'Direct Admission', 
      subtitle: 'Get priority access to top-ranked institutions.',
      color: 'from-teal-600 to-teal-700',
      icon: GraduationCap,
      tag: 'Partner Universities',
      theme: {
        button: 'bg-teal-600 hover:bg-teal-700',
        ring: 'focus:ring-teal-600',
        border: 'focus:border-teal-600',
        iconColor: 'text-teal-600',
        lightBg: 'bg-teal-50'
      }
    },
    { 
      id: 'template9', 
      title: 'Study in 20+ Countries', 
      subtitle: 'Explore endless opportunities across the globe.',
      color: 'from-cyan-600 to-cyan-700',
      icon: Globe2,
      tag: 'Global Study',
      theme: {
        button: 'bg-cyan-600 hover:bg-cyan-700',
        ring: 'focus:ring-cyan-600',
        border: 'focus:border-cyan-600',
        iconColor: 'text-cyan-600',
        lightBg: 'bg-cyan-50'
      }
    },
    { 
      id: 'template10', 
      title: 'Start Your Journey Today', 
      subtitle: 'Get started in minutes, not weeks.',
      color: 'from-amber-600 to-amber-700',
      icon: Zap,
      tag: 'Quick Start',
      theme: {
        button: 'bg-amber-600 hover:bg-amber-700',
        ring: 'focus:ring-amber-600',
        border: 'focus:border-amber-600',
        iconColor: 'text-amber-600',
        lightBg: 'bg-amber-50'
      }
    },
  ];

  // Logic to determine which template to show
  const [activeTemplate, setActiveTemplate] = useState(defaultTemplates[0]);

  useEffect(() => {
    if (forceOpen) setIsOpen(true);

    if (customTemplateData) {
      // 1. Priority: Live Preview Data passed from Admin
      setActiveTemplate(customTemplateData);
    } else if (templateId) {
      // 2. Specific Template ID requested
      const found = defaultTemplates.find(t => t.id === templateId);
      if (found) setActiveTemplate(found);
    } else {
      // 3. Random logic (if not preview)
      if (!isPreview) {
        getPopupSettings().then(settings => {
          const enabled = settings.enabled_templates || ['template1'];
          if (enabled.length > 0) {
            const randomId = enabled[Math.floor(Math.random() * enabled.length)];
            const found = defaultTemplates.find(t => t.id === randomId);
            if (found) setActiveTemplate(found);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, forceOpen, customTemplateData, isPreview]);

  // Handle Form Input
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle Close
  const handleClose = () => {
    if (typeof onClose === 'function') onClose();
    setIsOpen(false);
  };

  // Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!isPreview) {
        await saveLead({
          ...formData,
          source: 'random_popup',
          type: activeTemplate.tag?.toLowerCase().replace(/\s+/g, '_') || 'general',
          timestamp: new Date().toISOString(),
        });
      } else {
        // Fake delay for preview
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
      setIsSuccess(true);
      if (!isPreview) {
        setTimeout(() => handleClose(), 3000);
      }
    } catch (error) {
      console.error('Error saving lead:', error);
      setIsSuccess(true); // Fallback success UI
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation Variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { type: "spring", duration: 0.5 } },
    exit: { opacity: 0, scale: 0.95, y: 20 }
  };

  const IconComponent = activeTemplate.icon || Sparkles;
  const theme = activeTemplate.theme || defaultTemplates[0].theme;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm"
      >
        <motion.div 
          variants={modalVariants}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden relative flex flex-col md:flex-row max-h-[90vh] md:max-h-none md:min-h-[500px]"
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-30 p-2 bg-black/20 md:bg-white/10 hover:bg-black/40 rounded-full text-white md:text-slate-200 md:hover:text-white transition-colors duration-200"
          >
            <X size={20} />
          </button>

          {/* LEFT SIDE: Visual Banner */}
          <div className={`hidden md:flex w-2/5 bg-gradient-to-br ${activeTemplate.color} relative flex-col justify-between p-10 text-white overflow-hidden`}>
            {/* Background Decorations */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 bg-white/20 border border-white/30 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-6">
                <IconComponent size={12} /> {activeTemplate.tag}
              </div>
              <h2 className="text-4xl font-serif font-bold leading-tight mb-4">
                {activeTemplate.title}
              </h2>
              <p className="text-white/90 text-sm leading-relaxed">
                {activeTemplate.subtitle}
              </p>
            </div>

            <div className="relative z-10 opacity-90">
              <div className="flex gap-1 mb-2">
                {[1,2,3,4,5].map(i => <Star key={i} size={14} className="text-yellow-300 fill-yellow-300" />)}
              </div>
              <p className="text-xs font-medium">Trusted by 5,000+ students</p>
            </div>
          </div>

          {/* Mobile Header (Only visible on small screens) */}
          <div className={`md:hidden bg-gradient-to-br ${activeTemplate.color} relative p-6 text-white shrink-0 overflow-hidden`}>
            <div className="relative z-10 pr-8">
              <div className="inline-flex items-center gap-2 bg-white/20 border border-white/30 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3">
                <IconComponent size={10} /> {activeTemplate.tag}
              </div>
              <h2 className="text-2xl font-serif font-bold leading-tight">
                {activeTemplate.title}
              </h2>
            </div>
          </div>

          {/* RIGHT SIDE: Form Section */}
          <div className="w-full md:w-3/5 p-6 md:p-12 bg-white flex flex-col justify-center relative overflow-y-auto">
            {isSuccess ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-10"
              >
                <div className={`w-16 h-16 md:w-20 md:h-20 ${theme.lightBg} rounded-full flex items-center justify-center mx-auto mb-6 ${theme.iconColor}`}>
                  <CheckCircle2 size={32} />
                </div>
                <h3 className="text-2xl md:text-3xl font-serif font-bold text-slate-900 mb-2">You're in!</h3>
                <p className="text-slate-500 mb-6 text-sm md:text-base">
                  Our senior counsellor will call you within 24 hours to discuss your profile.
                </p>
                <button onClick={handleClose} className={`text-sm font-bold text-slate-400 hover:${theme.iconColor} underline`}>
                  Close Window
                </button>
              </motion.div>
            ) : (
              <>
                <div className="mb-6 md:mb-8">
                  <h3 className="text-xl md:text-3xl font-serif font-bold text-slate-900 mb-2">
                    Start Your Journey
                  </h3>
                  <p className="text-slate-500 text-xs md:text-sm">
                    Fill the form below to claim this offer.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
                  <div className="relative group">
                    <User className={`absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:${theme.iconColor} transition-colors`} size={18} />
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Full Name"
                      className={`w-full pl-12 pr-4 py-3 md:py-4 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white ${theme.border} focus:ring-1 ${theme.ring} outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium text-sm md:text-base`}
                    />
                  </div>

                  <div className="relative group">
                    <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:${theme.iconColor} transition-colors`} size={18} />
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email Address"
                      className={`w-full pl-12 pr-4 py-3 md:py-4 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white ${theme.border} focus:ring-1 ${theme.ring} outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium text-sm md:text-base`}
                    />
                  </div>

                  <div className="relative group">
                    <Phone className={`absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:${theme.iconColor} transition-colors`} size={18} />
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Phone Number"
                      className={`w-full pl-12 pr-4 py-3 md:py-4 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white ${theme.border} focus:ring-1 ${theme.ring} outline-none transition-all placeholder:text-slate-400 text-slate-900 font-medium text-sm md:text-base`}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full ${theme.button} text-white font-bold py-3 md:py-4 rounded-xl shadow-lg transition-all hover:scale-[1.02] flex items-center justify-center gap-2 mt-2 text-sm md:text-base`}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin" size={18} /> Processing...
                      </>
                    ) : (
                      <>
                        Request Call Back <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                  
                  <p className="text-center text-[10px] text-slate-400 mt-2">
                    By clicking submit, you agree to our Terms & Privacy Policy.
                  </p>
                </form>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RandomPopup;